'use client'
import React, { useImperativeHandle, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Button, Heading, Picture, Text } from '../../components/index.js'
import { withLayout } from '../../shared/Styles.js'
import type { MemberEquityProps } from './types.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { isVideo } from '../../shared/mimeType.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

const componentType = 'image'
const componentName = 'member_area'

const MemberEquity: React.FC<MemberEquityProps> = React.forwardRef<HTMLDivElement, MemberEquityProps>(
  ({ data, className = '', ...rest }, ref) => {
    const {
      title,
      description,
      backgroundImage,
      mobileBackgroundImage,
      primaryButton,
      secondaryButton,
      theme,
      shape,
      primaryButtonHandler,
      secondaryButtonHandler,
    } = data

    const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
    const innerRef = useRef<HTMLDivElement>(null)
    const { locale = 'us' } = useAiuiContext()

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

    useExposure(innerRef, {
      componentType,
      componentName,
      componentTitle: title,
      componentDescription: description,
    })

    const handleButtonPrimaryClick = () => {
      // 执行主按钮的点击事件
      console.log('primaryButtonHandler', primaryButtonHandler)
      primaryButtonHandler?.()
    }

    const handleButtonSecondaryClick = () => {
      // 执行副按钮的点击事件
      secondaryButtonHandler?.()
    }

    const lgButtonSize = 'lg-desktop:px-7 lg-desktop:pb-[14px] lg-desktop:pt-[15px] lg-desktop:text-[16px]'

    return (
      <section
        data-ui-component-id="MemberEquity"
        {...rest}
        className={cn(
          'lg-desktop:aspect-[1384/560] desktop:aspect-[1090/488] laptop:aspect-[774/336] tablet:aspect-[704/480]',
          'text-info-primary relative overflow-hidden',
          className,
          {
            'aiui-dark': theme === 'dark',
            'rounded-box': shape === 'rounded',
            'h-[480px]': isMobile,
          }
        )}
        ref={innerRef}
      >
        {isVideo(backgroundImage?.mimeType) ? (
          <video
            autoPlay
            muted
            playsInline
            loop
            className="laptop:block absolute inset-0 hidden size-full object-cover"
          >
            <source src={backgroundImage?.url} type="video/mp4" />
          </video>
        ) : (
          <Picture
            source={backgroundImage?.url}
            alt={backgroundImage?.alt}
            className="laptop:block absolute inset-0 hidden size-full object-cover [&_img]:h-full [&_img]:object-cover"
          />
        )}
        {isVideo(mobileBackgroundImage?.mimeType) ? (
          <video autoPlay muted playsInline loop className="laptop:hidden absolute inset-0 size-full object-cover">
            <source src={mobileBackgroundImage?.url} type="video/mp4" />
          </video>
        ) : (
          <Picture
            source={mobileBackgroundImage?.url}
            alt={mobileBackgroundImage?.alt}
            className="laptop:hidden absolute inset-0 size-full object-cover [&_img]:h-full [&_img]:object-cover"
          />
        )}
        <div className={'laptop:items-center absolute inset-0 flex w-full items-end py-[16px]'}>
          <div
            className={`
              tablet:px-6 laptop:px-[76px] desktop:px-[111px] lg-desktop:px-[139px] tablet:gap-5 
              laptop:gap-4 desktop:gap-6 lg-desktop:gap-8 flex w-full flex-col gap-6 px-4
            `}
          >
            <div className="desktop:gap-2 lg-desktop:gap-4 laptop:max-w-[286px] desktop:max-w-[426px] lg-desktop:max-w-[547px] flex flex-col">
              <Heading as="h2" size={3} className={`member-equity-title `} html={title} />
              <Text
                size={2}
                className={`member-equity-description lg-desktop:text-[18px] desktop:text-[16px] text-[14px]`}
                html={description}
              />
            </div>
            {/* 按钮组 */}
            <div className="lg-desktop:gap-3 flex items-center gap-2">
              {secondaryButton && secondaryButton.text && (
                <Button
                  aria-label={title ?? description}
                  variant="secondary"
                  onClick={handleButtonSecondaryClick}
                  className={cn('member-equity-button-secondary ', lgButtonSize)}
                  as="a"
                  href={trackUrlRef(
                    getLocalizedPath(secondaryButton.link || '', locale),
                    `${componentType}_${componentName}`
                  )}
                  data-headless-type-name={`${componentType}#${componentName}`}
                  data-headless-title-desc-button={`${title}#${description}#${secondaryButton.text}`}
                >
                  {secondaryButton.text}
                  <span className="sr-only">{title ?? description}</span>
                </Button>
              )}
              {primaryButton && primaryButton.text && (
                <Button
                  variant="primary"
                  onClick={handleButtonPrimaryClick}
                  className={cn('member-equity-button-primary ', lgButtonSize)}
                  data-headless-type-name={`${componentType}#${componentName}`}
                  data-headless-title-desc-button={`${title}#${description}#${primaryButton.text}`}
                >
                  {primaryButton.text}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    )
  }
)

MemberEquity.displayName = 'MemberEquity'

export default withLayout(MemberEquity)
